import type { Communication, Patient } from '@medplum/fhirtypes'
import { createReference } from '@medplum/core'
import { useMedplum } from '@medplum/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { careCoordinationNotesQueryKey } from './useCareCoordinationNotes'
import { CATEGORY_CODE, CATEGORY_SYSTEM, buildCommunicationPayload, uploadFiles } from './utils'

type CreateVars = { note: string; nextAction: string; files: File[] }

export function useCreateCareCoordinationNote(patient: Patient) {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  return useMutation<Communication, Error, CreateVars>({
    mutationFn: async ({ note, nextAction, files }) => {
      const profile = medplum.getProfile()
      const attachments = await uploadFiles(medplum, patient, files)
      return medplum.createResource<Communication>({
        resourceType: 'Communication',
        status: 'completed',
        category: [
          {
            coding: [
              { system: CATEGORY_SYSTEM, code: CATEGORY_CODE, display: 'Care Coordination Note' },
            ],
          },
        ],
        subject: createReference(patient),
        ...(profile && { sender: createReference(profile) }),
        sent: new Date().toISOString(),
        payload: buildCommunicationPayload(note, nextAction, attachments),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: careCoordinationNotesQueryKey(patient.id!) })
    },
    onError: (e) => {
      logError('patient', 'Failed to create care coordination note', e)
      showNotification({ title: 'Error', message: 'Failed to create note', color: 'red' })
    },
  })
}
